import { invoke } from '@shared/easyfy.decorators';
import { element, elementsOf, globalEvent } from '@shared/easyfy.core';
import { addCss, replaceHtml, toggleClass } from '@shared/easyfy.utils';
import { animationEnded, createElementWithClasses, createLink, isMobile } from '@clientShared/utils';

@invoke
export default class SalesUnitSelector {
  static Instance: SalesUnitSelector;

  sliderTransformStyle: string = '';

  constructor() {}

  onInit() {
    globalEvent({
      type: 'click',
      events: [
        {
          event: SalesUnitEvents.Open,
          callback: this.openSelector,
        },
        {
          event: SalesUnitEvents.Close,
          callback: this.closeSelector,
        },
        {
          event: 'sales-unit-select',
          callback: this.selectSalesUnit,
        },
        {
          callback: this.closeSelector,
        },
      ],
    });

    document.addEventListener(SalesUnitEvents.Close, () => {
      this.closeSelector();
    });
  }

  // ================================
  // Open, close and select
  // ================================
  openSelector = (e: Event) => {
    this.closeSelector();

    const currentSalesUnitDisplay: HTMLElement = e?.target as HTMLElement;
    const activeProductCard: HTMLElement = this.getActiveProductCard(currentSalesUnitDisplay);
    const salesUnitPopup: HTMLElement = this.getSalesUnitPopup(activeProductCard);
    const availableSalesUnits: HTMLElement = this.getAvailableSalesUnits(activeProductCard);
    const isCompactProductCard: boolean = this.getIsCompactProductCard(activeProductCard);

    if (!activeProductCard || !salesUnitPopup || !availableSalesUnits || !currentSalesUnitDisplay) {
      console.error('Could not open sales unit selector');
      return;
    }

    if (isCompactProductCard) {
      this.openCompactCard(currentSalesUnitDisplay, activeProductCard, salesUnitPopup);
    } else {
      this.openRegularCard(currentSalesUnitDisplay, activeProductCard, salesUnitPopup, availableSalesUnits);
    }
  };

  closeSelector = (e?: Event) => {
    const target = e?.target as HTMLElement | undefined;
    const activeProductCard = this.getOpenSalesUnitProductCardContainer();
    const isCompactProductCard = this.getIsCompactProductCard(activeProductCard);

    if (!activeProductCard || (target && target.hasAttribute('salesunit-open'))) return;

    if (isCompactProductCard) {
      this.closeCompactCard(activeProductCard);
    } else {
      this.closeRegularCard(activeProductCard);
    }
  };

  selectSalesUnit = (e: Event) => {
    const target: HTMLElement = e?.target as HTMLElement;

    if (!target) return; // add snackbar

    const clickedSalesUnit = this.getClickedSalesUnit(target);
    const productContainer: HTMLElement = this.getOpenSalesUnitProductCardContainer() ?? target.closest('[data-container="product"]');
    const activeProductCard: HTMLElement = this.getActiveProductCard(target);
    const isCompactProductCard: boolean = this.getIsCompactProductCard(activeProductCard);

    this.removeCurrentlySelectedSalesUnit(clickedSalesUnit.element);
    clickedSalesUnit.element?.setAttribute('data-selected', '');
    this.updateBuyButtonOnSelect(clickedSalesUnit, productContainer);
    this.updateFlagVisibility(clickedSalesUnit.salesUnitCode, productContainer);

    if (this.isProductPage(clickedSalesUnit.element)) {
      this.updateProductPageHtmlOnSelect(clickedSalesUnit, productContainer);
    } else {
      this.updateProductCardHtmlOnSelect(clickedSalesUnit, activeProductCard, isCompactProductCard);
    }

    if (!isMobile() || isCompactProductCard) this.closeSelector();
  };

  // ================================
  // Helpers when opening selector
  // ================================

  openCompactCard = (currentSalesUnitDisplay: HTMLElement, activeProductCard: HTMLElement, salesUnitPopup: HTMLElement) => {
    toggleClass(activeProductCard, 'salesunit-open');
    salesUnitPopup.setAttribute('salesunit-open', '');
    this.addEvent(currentSalesUnitDisplay, SalesUnitEvents.Close);
  };

  openRegularCard = (currentSalesUnitDisplay: HTMLElement, activeProductCard: HTMLElement, salesUnitPopup: HTMLElement, availableSalesUnits: HTMLElement) => {
    if (isMobile()) {
      this.disableSlider(currentSalesUnitDisplay);
      this.wrapProductInfoWithLink(activeProductCard, salesUnitPopup);
      this.createMobileSlideUp(activeProductCard, salesUnitPopup);
    } else {
      addCss(availableSalesUnits, 'desktop');
      toggleClass(activeProductCard, 'salesunit-open');
      salesUnitPopup.setAttribute('salesunit-open', '');
    }
  };

  addEvent = (element: HTMLElement, event: string) => {
    if (!element || !event) return console.error('Could not add event to element');

    element.dataset.event = event;
  };

  createMobileSlideUp = (activeProductCard: HTMLElement, salesUnitPopup: HTMLElement) => {
    let salesUnitsContainer: HTMLElement = document.querySelector('[data-salesunitselector-mobile-container]');

    if (!salesUnitsContainer) {
      salesUnitsContainer = this.createSalesUnitsMobileContainer();
    } else {
      salesUnitsContainer.innerText = '';
      salesUnitsContainer.classList.add('salesunit-open');
    }

    this.updateAvailableSalesUnits(salesUnitsContainer, activeProductCard, salesUnitPopup);

    if (!document.body.contains(salesUnitsContainer)) {
      document.body.appendChild(salesUnitsContainer);
    }

    document.body.classList.add('noof');
  };

  createSalesUnitsMobileContainer = (): HTMLElement => {
    const salesUnitsContainer = createElementWithClasses('div', ['salesunitselector-mobile-container', 'productcard-regular', 'salesunit-open']);
    salesUnitsContainer.dataset.container = 'product-card';
    salesUnitsContainer.setAttribute('data-salesunitselector-mobile-container', '');
    return salesUnitsContainer;
  };

  updateAvailableSalesUnits = (salesUnitsContainer: HTMLElement, activeProductCard: HTMLElement, salesUnitPopup: HTMLElement) => {
    const salesUnitPopupClone = this.cloneSalesUnitPopup(salesUnitPopup);

    this.appendButtonIfExists(salesUnitPopupClone, activeProductCard);

    salesUnitsContainer?.appendChild(salesUnitPopupClone);
  };

  cloneSalesUnitPopup(salesUnitPopup: HTMLElement): HTMLElement {
    const salesUnitPopupClone = salesUnitPopup.cloneNode(true) as HTMLElement;
    salesUnitPopupClone?.setAttribute('salesunit-open', '');
    salesUnitPopupClone?.removeAttribute('data-salesunit-closing');
    return salesUnitPopupClone;
  }

  appendButtonIfExists(salesUnitPopupClone: HTMLElement, activeProductCard: HTMLElement) {
    const button = this.getProductCardButton(activeProductCard);
    if (button) salesUnitPopupClone?.insertAdjacentHTML('beforeend', button.outerHTML);
  }

  wrapProductInfoWithLink = (activeProductCard: HTMLElement, salesUnitPopup: HTMLElement) => {
    if (!activeProductCard || !salesUnitPopup) return;

    const url = (activeProductCard.querySelector('[data-container="product-link"]') as HTMLAnchorElement | null)?.href;
    if (!url) return;

    const productInfo = salesUnitPopup.querySelector('[data-container="product-info"]') as HTMLElement | null;
    if (!productInfo || !productInfo.parentNode) return;

    const newLink = createLink(url, productInfo.innerHTML, 'product-info');
    newLink.dataset.container = 'product-info';

    productInfo.parentNode.replaceChild(newLink, productInfo);
  };

  getProductCardButton(activeProductCard: HTMLElement): HTMLElement | null {
    return (
      activeProductCard?.querySelector('[data-container="addtocart-btn"]') ??
      activeProductCard?.querySelector('[data-container="view-product"]') ??
      activeProductCard?.querySelector('[data-action="subscribe"]')
    );
  }

  // ================================
  // Helpers selecting sales unit
  // ================================

  getClickedSalesUnit = (clickedSalesUnit: HTMLElement): SalesUnitElement => {
    return {
      element: clickedSalesUnit,
      productId: clickedSalesUnit.dataset.value,
      salesUnitCode: clickedSalesUnit.dataset.salesunitcode,
      salesUnit: clickedSalesUnit.querySelector('[data-container="packsize"]')?.attributes['data-packsize']?.value,
      unitPrice: clickedSalesUnit.querySelector('[data-container="unit-price"]')?.innerHTML,
      upsellId: clickedSalesUnit.dataset.upsellid,
    };
  };

  isProductPage = (clickedSalesUnit: HTMLElement): boolean => {
    return !!clickedSalesUnit.closest('[data-container="product"]');
  };

  removeCurrentlySelectedSalesUnit = (element: HTMLElement) => {
    if (!element?.parentElement) return console.error('Parent element is missing');
    const selectedVariant: HTMLElement = elementsOf(element?.parentElement, '[data-container="salesunit-option"][data-selected]');
    selectedVariant?.removeAttribute('data-selected');
  };

  updateBuyButtonOnSelect = (clickedSalesUnit: SalesUnitElement, productContainer: HTMLElement) => {
    const buyButton = this.getActiveBuyButton(clickedSalesUnit, productContainer);

    if (!buyButton || !clickedSalesUnit.productId) return console.error('Error updating buy button');

    buyButton.dataset.productid = clickedSalesUnit.productId;
    buyButton.dataset.salesunitcode = clickedSalesUnit.salesUnitCode;
    buyButton.dataset.upsellid = clickedSalesUnit.upsellId;
  };

  getActiveBuyButton = (clickedSalesUnit: SalesUnitElement, productContainer: HTMLElement): HTMLElement => {
    if (this.isProductPage(clickedSalesUnit.element)) {
      return productContainer?.querySelector('[data-container="addtocart-btn"]');
    } else {
      return document.querySelector('[salesunit-open]')?.closest('[data-container="product-card"]')?.querySelector('[data-container="addtocart-btn"]');
    }
  };

  updateProductCardHtmlOnSelect = (clickedSalesUnit: SalesUnitElement, activeProductCard: HTMLElement, isCompactProductCard: boolean) => {
    if (!activeProductCard) return console.error('Active product card is missing');

    const currentSalesUnit: HTMLElement = elementsOf(activeProductCard, '[data-container="current-salesunit-info"]');
    const priceContainer: HTMLElement = elementsOf(activeProductCard, '[data-container="price-container"]');

    if (!currentSalesUnit || !activeProductCard || !clickedSalesUnit.element || !priceContainer) return;

    if (isMobile() && !isCompactProductCard) return;

    currentSalesUnit.innerHTML = clickedSalesUnit.element.innerHTML;

    if (!priceContainer.hasAttribute('data-unitprice')) {
      priceContainer.setAttribute('data-unitprice', '');
    }
    if (!priceContainer.hasAttribute('data-packsize')) {
      priceContainer.setAttribute('data-packsize', '');
    }

    priceContainer.attributes['data-unitprice'].value = clickedSalesUnit.unitPrice;
    priceContainer.attributes['data-packsize'].value = clickedSalesUnit.salesUnit;

    if (isCompactProductCard) {
      const price: HTMLElement = elementsOf(currentSalesUnit, '.price');
      const pricehtml = `<span class="price"><span data-container="price">${price?.innerHTML}</span><i class="icon icon-chevron-down size-xs clr-black"></i></span>`;
      replaceHtml(price, pricehtml);
    } else {
      const packsize: HTMLElement = elementsOf(currentSalesUnit, '.packsize');
      const packsizehtml = `<span class="packsize">${packsize?.innerHTML}<i class="icon icon-chevron-down size-xs clr-black"></i></span>`;
      replaceHtml(packsize, packsizehtml);
    }
  };

  updateProductPageHtmlOnSelect = (clickedSalesUnit: SalesUnitElement, productContainer: HTMLElement) => {
    if (!clickedSalesUnit?.element || !productContainer) return console.error('Element is missing');
    const currentSalesUnitDisplay: HTMLElement = elementsOf(productContainer, '[data-container="current-salesunit-info"]');

    if (!currentSalesUnitDisplay) return console.error('Current sales unit display is missing');

    const selectedSalesUnitInnerText: string = elementsOf(clickedSalesUnit.element, '.packsize')?.innerText;
    const selectedPriceInnerText: string = elementsOf(clickedSalesUnit.element, '.price')?.innerText;
    const displayPrice: HTMLElement = elementsOf(currentSalesUnitDisplay, '[data-container="selected-price"]');
    const displaySalesUnit: HTMLElement = elementsOf(currentSalesUnitDisplay, '[data-container="selected-salesunit"]');

    if (!displayPrice || !displaySalesUnit || !selectedSalesUnitInnerText || !selectedPriceInnerText)
      return console.error('Display price or sales unit is missing');

    displayPrice.innerText = selectedPriceInnerText;
    displaySalesUnit.innerText = selectedSalesUnitInnerText;
  };

  updateFlagVisibility = (clickedSalesUnitCode: string, productContainer: HTMLElement) => {
    const flags: NodeListOf<HTMLElement> = productContainer?.querySelectorAll('.flag[data-salesunit]');

    if (!flags || !clickedSalesUnitCode) return;

    const currentVisibleFlags = Array.from(flags).filter((flag) => flag.hasAttribute('data-current'));
    currentVisibleFlags.forEach((flag) => {
      flag.removeAttribute('data-current');
    });

    const newVisibleFlags = Array.from(flags).filter((flag) => flag.getAttribute('data-salesunit') === clickedSalesUnitCode);
    newVisibleFlags.forEach((flag) => {
      flag.setAttribute('data-current', 'true');
    });
  };

  // ================================
  // Helpers when closing selector
  // ================================

  closeCompactCard = (activeProductCard: HTMLElement) => {
    const selectedVariant: HTMLElement = activeProductCard?.querySelector('[data-container="current-salesunit-info"]');
    const salesUnitPopup: HTMLElement = this.getSalesUnitPopup(activeProductCard);

    salesUnitPopup?.removeAttribute('salesunit-open');
    activeProductCard.classList.remove('salesunit-open');
    this.addEvent(selectedVariant, SalesUnitEvents.Open);
  };

  closeRegularCard = (activeProductCard: HTMLElement) => {
    const container: HTMLElement = isMobile() ? document.querySelector('.salesunitselector-mobile-container') : activeProductCard;
    const salesUnitPopup: HTMLElement = this.getSalesUnitPopup(container);
    const button: HTMLElement = salesUnitPopup?.querySelector('[data-container="addtocart-btn"]');

    if (!salesUnitPopup) return; // add snackbar

    salesUnitPopup.setAttribute('data-salesunit-closing', '');

    animationEnded(salesUnitPopup, () => {
      salesUnitPopup.removeAttribute('data-salesunit-closing');
      salesUnitPopup.removeAttribute('salesunit-open');
      toggleClass(container, 'salesunit-open');

      if (isMobile()) {
        document.body.classList.remove('noof');
        button?.remove();
        this.enableSlider();
      }
    });
  };

  // ================================
  // Helpers
  // ================================

  getActiveProductCard = (target: HTMLElement): HTMLElement => target?.closest('[data-container="product-card"]');
  getSalesUnitPopup = (element: HTMLElement): HTMLElement => element?.querySelector('[data-container="salesunit-popup"]');
  getAvailableSalesUnits = (element: HTMLElement): HTMLElement => element?.querySelector('.selector');
  getIsCompactProductCard = (element: HTMLElement): boolean => element?.hasAttribute('product-card-compact');

  getOpenSalesUnitProductCardContainer = (): HTMLElement => {
    return document.querySelector('[salesunit-open]')?.closest('[data-container="product-card"]');
  };

  disableSlider = (target: HTMLElement) => {
    const slider = target.closest('[data-container="productlist-scroll"]') as HTMLElement | null;
    if (!slider) return;
    toggleClass(slider, 'disable-slider');

    const listElement = slider.querySelector('.splide__list') as HTMLElement | null;
    this.sliderTransformStyle = listElement?.getAttribute('style') ?? '';
  };

  enableSlider = () => {
    const disabledSlider: HTMLElement = element('[data-container="productlist-scroll"].disable-slider');
    toggleClass(disabledSlider, 'disable-slider');
    (disabledSlider?.querySelector('.splide__list') as HTMLElement)?.setAttribute('style', this.sliderTransformStyle);
  };
}

export interface SalesUnitElement {
  element: HTMLElement;
  productId: string;
  salesUnitCode: string;
  salesUnit: number;
  unitPrice: string;
  upsellId: string;
}

export enum SalesUnitEvents {
  Close = 'sales-units-close',
  Open = 'sales-units-open',
}
